import _ from 'underscore';
import Promise from 'bluebird';
import { constants, ImageExperienceImage } from '@bingads-webui-campaign-react/image-experience';
import { serializeImageAssetMetadata } from '@bingads-webui-campaign/media-metadata-model';
import { getMMAPayloadV2 } from './data-process';
import { findMetadata, insertMetadata, isMetadataExisting, collectExistingMetadata } from './image-metadata-util';
import { convertToLowerCaseKey } from './format-data-util';

const removeRatioTypeInId = (flattenImageList) => {
  flattenImageList.forEach((image) => {
    // eslint-disable-next-line no-param-reassign
    image.id = image.id.toString().indexOf('-') > -1 && image.isLogo ? image.id.match(/(\S*)-/)[1] : image.id;
  });
  return flattenImageList;
};

const getMMAImageList = imageList =>
  _.map(
    imageList,
    imageArray => _.filter(
      imageArray,
      imageObj => imageObj.type in constants.getMmaAspectRatios() && imageObj.isIncluded // eslint-disable-line max-len
    )
  );

export const getFlattenedImageAssetList = (imageList, isMultimediaAdinMSANEnabled, isMultimediaAd) => {
  let filteredImageList = imageList;

  if (isMultimediaAdinMSANEnabled) {
    filteredImageList = _.map(
      imageList,
      imageArray => _.filter(imageArray, imageObj => imageObj.isIncluded)
    );
  } else if (isMultimediaAd) {
    filteredImageList = getMMAImageList(imageList);
  }
  const flattenImageList = _.flatten(filteredImageList, 1);
  return removeRatioTypeInId(flattenImageList);
};

// flattenedImageAssetList and flattenedImageAssetsPayload are mutually exclusive
// only one will be used at one time
export const getImagesDataModel = ({
  flattenedImageAssetList, updates, flattenedImageAssetsPayload, isMultimediaAd, UiUnsupportedRatios,
}) => {
  const imagesModel = flattenedImageAssetsPayload || _.map(
    flattenedImageAssetList,
    (imageObj, index) => {
      const overrideInfo = updates && updates[index];
      const model = imageObj.formatToSave(isMultimediaAd, false, overrideInfo);

      return model;
    }
  );

  const UIUnSupportedImagesModel = convertToLowerCaseKey(UiUnsupportedRatios
    .map(association =>
      _.pick(association.Asset, ['Id', 'CropWidth', 'CropHeight', 'CropX', 'CropY', 'IdString', 'SubType'])));

  return {
    Images: JSON.stringify([...imagesModel, ...UIUnSupportedImagesModel]),
  };
};

export const setMTImageModel = (
  originalData,
  updates = {},
  imageList,
  options = {}, // isMultimediaAdinMSANEnabled, isMultimediaAd, isMMAV2Enabled
  UiUnsupportedRatios,
  portraitImage = {}
) => {
  const flattenedImageList = getFlattenedImageAssetList(imageList, options.isMultimediaAdinMSANEnabled, options.isMultimediaAd);
  if (options.isMultimediaAdinMSANEnabled) {
    let payload = getMMAPayloadV2(
      flattenedImageList.filter(item => !item.isLogo),
      updates,
      options.isMMAV2Enabled,
      options.isBoost200x100ImageEnabled,
      options.isBoostNewAspectRatioSupportEnabled
    );

    payload = payload.concat(flattenedImageList
      .filter(item => item.isLogo)
      .map(item => item.formatToSave()));

    if (options.isBoostPortraitImageEnabled && portraitImage instanceof ImageExperienceImage) {
      const portraitImagePayload = portraitImage.formatToSave();
      payload.push(portraitImagePayload);
    }

    _.extend(originalData, getImagesDataModel({
      flattenedImageAssetsPayload: payload,
      isMultimediaAd: options.isMultimediaAd,
      UiUnsupportedRatios,
    }));
  } else {
    _.extend(originalData, getImagesDataModel({
      flattenedImageAssetList: flattenedImageList,
      updates,
      isMultimediaAd: options.isMultimediaAd,
      UiUnsupportedRatios,
    }));
  }
};

// If applying effect to an image, need to upload to get a new image Id
export const updateAssetsIdsAsync = (flattenedImageAssetList, imageIndexAsThumbnail, isAdCreatorEnabled, imageOData, isInlineAssetEditorRefreshEnabled) => {
  const updates = {};

  if (isAdCreatorEnabled) {
    const assetPositionsToUpdate = [];
    const newAssets = [];

    // use this mapping to update id. collect metadata-id mapping from existing assets
    const existingMetadataIdMapping = collectExistingMetadata(flattenedImageAssetList);

    _.each(flattenedImageAssetList, (asset, index) => {
      const metadata = serializeImageAssetMetadata({ effectSettings: asset.EffectSettings }, true, isInlineAssetEditorRefreshEnabled);

      // check if any assets have updates in metadata, if so, need to update id
      if (asset.isEffectEdited({ isInlineAssetEditorRefreshEnabled })) {
        assetPositionsToUpdate.push({ position: index, baseId: asset.BaseId, metadata });

        if (!isMetadataExisting(existingMetadataIdMapping, asset.BaseId, metadata)) {
          newAssets.push({
            url: asset.FinalSrc,
            baseId: asset.BaseId,
            metadata,
          });
        }
      }
    });

    if (!_.isEmpty(assetPositionsToUpdate)) {
      const saveNewImagesPromise = _.isEmpty(newAssets)
        ? Promise.resolve([]) : imageOData.bulkSaveImagesByUrl(newAssets);

      return saveNewImagesPromise
        .then((ids) => {
          const hasError = _.some(ids, id => id && id.code);

          if (hasError) {
            const error = {
              errors: _.compact(_.map(ids, id => id.code && {
                Error: id.code,
              })),
            };

            // reject to reuse common catch logic
            return Promise.reject(error);
          }

          _.each(ids, (id, index) => {
            const { baseId, metadata } = newAssets[index];
            insertMetadata(existingMetadataIdMapping, baseId, metadata, {
              id,
              bingSrc: undefined, // for new assets, we don't have bing url yet
            });
          });

          _.each(assetPositionsToUpdate, ({ position, baseId, metadata }) => {
            const foundInfo = findMetadata(existingMetadataIdMapping, baseId, metadata);

            updates[position] = {
              id: foundInfo.id,
              bingSrc: foundInfo.bingSrc,
            };
          });

          // if asset used for thumbnail is updated without latest bing src, send call
          if (updates[imageIndexAsThumbnail] && !updates[imageIndexAsThumbnail].bingSrc) {
            const thumbnailAsset = updates[imageIndexAsThumbnail];

            return imageOData.getImageByIdAsync(thumbnailAsset.id)
              .then((value) => {
                thumbnailAsset.bingSrc = value.Url;

                return updates;
              });
          }

          return updates;
        });
    }
  }

  return Promise.resolve(updates);
};

export const processImageListViewModelToModel = (imageList, imageOData, isMultimediaAd = true, isMultimediaAdinMSANEnabled = false, isMMAV2Enabled = false, UiUnsupportedRatios = [], isInlineAssetEditorRefreshEnabled = false) => {
  const flattenedIncludedImageAssetList = getFlattenedImageAssetList(imageList, isMultimediaAdinMSANEnabled, isMultimediaAd);
  const imageIndexAsThumbnail = 0;
  const originalData = {};

  return updateAssetsIdsAsync(flattenedIncludedImageAssetList, imageIndexAsThumbnail, true /* isAdCreatorEnabled */, imageOData, isInlineAssetEditorRefreshEnabled)
    .then((updates) => {
      const originalThumbnailAsset = flattenedIncludedImageAssetList[imageIndexAsThumbnail];
      const overrideInfo = updates[imageIndexAsThumbnail];
      let imageToGenerateThumbnail;

      if (overrideInfo) {
        imageToGenerateThumbnail = originalThumbnailAsset.copyThumbnailLookToNewImage({
          id: overrideInfo.id,
          src: overrideInfo.bingSrc,
        });
      } else {
        imageToGenerateThumbnail = originalThumbnailAsset;
      }

      setMTImageModel(
        originalData,
        updates,
        imageList,
        {
          isMultimediaAdinMSANEnabled,
          isMultimediaAd,
          isMMAV2Enabled,
          isBoostPortraitImageEnabled: false,
          isBoost200x100ImageEnabled: false,
          isBoostNewAspectRatioSupportEnabled: false,
        },
        UiUnsupportedRatios
      );

      return {
        mtModel: originalData,
        imageToGenerateThumbnail,
      };
    });
};
